import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import * as yup from "yup";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  checkArray,
  checkNull,
  createSelectOptions,
} from "../../../../helpers";
import {
  changeSettingValue,
  getApplicableQuarterDetails,
  getFinanceForecastSettings,
  getForecastRulesDetails,
  saveFinanceForecastSettings,
  editFinanceRuleDetail,
  updateFinanceRuleDetail,
} from "../../../../Redux/actions";
import Headerbar from "../../../Components/InnerLayout/Headerbar";
import UserInterface from "../../../Components/InnerLayout/UserInterface";
import ModelContainer from "../../../Components/ModelContainer";
import ErrorContainer from "../../../Components/ErrorContainer";
import LoadingIcon from "../../../Components/LoadingIcon";

const editRevenueRuleSchema = yup
  .object({
    id: yup.string().required(),
    financial_year: yup.string().required(),
    quarter: yup.string().required(),
    month: yup.string().required(),
    forecast_rule1: yup.string().required(),
    forecast_rule2: yup.string().nullable(),
    forecast_model_id: yup.string().required(),
    forecast_entry_rule1: yup.string().required(),
    forecast_entry_rule2: yup.string().nullable(),
  })
  .required();
const revenueRuleSchema = yup
  .object({
    financial_year: yup.string().required(),
    quarter: yup.string().required(),
    forecast_rule1: yup.string().required(),
    forecast_rule2: yup.string().nullable(),
    forecast_model: yup.string().required(),
    forecast_entry_rule1: yup.string().when("forecast_model", {
      is: (modelVal) => modelVal != 1,
      then: yup.string().required(),
    }),
    forecast_entry_rule2: yup
      .string()
      .when(["forecast_model", "forecast_rule1"], {
        is: (modelVal, forecastVal) =>
          modelVal != 1 && !["5", "6", "7", "8"].includes(forecastVal),
        then: yup.string().required(),
      }),
    month: yup.object().shape({
      mon1: yup.object().shape({
        forecast_entry_rule1: yup.number().when("forecast_model", {
          is: "1",
          then: yup.number().positive().required(),
        }),
        forecast_entry_rule2: yup
          .number()
          .when(["forecast_model", "forecast_rule1"], {
            is: (modelVal, forecastVal) =>
              modelVal == 1 && !["5", "6"].includes(forecastVal),
            then: yup.number().positive().required(),
          }),
      }),
      mon2: yup.object().shape({
        forecast_entry_rule1: yup.number().when("forecast_model", {
          is: 1,
          then: yup.number().positive().required(),
        }),
        forecast_entry_rule2: yup
          .number()
          .when(["forecast_model", "forecast_rule1"], {
            is: (modelVal, forecastVal) =>
              modelVal == 1 && !["5", "6"].includes(forecastVal),
            then: yup.number().positive().required(),
          }),
      }),
      mon3: yup.object().shape({
        forecast_entry_rule1: yup.number().when("forecast_model", {
          is: 1,
          then: yup.number().positive().required(),
        }),
        forecast_entry_rule2: yup
          .number()
          .when(["forecast_model", "forecast_rule1"], {
            is: (modelVal, forecastVal) =>
              modelVal == 1 && !["5", "6"].includes(forecastVal),
            then: yup.number().positive().required(),
          }),
      }),
    }),
  })
  .required();

const costSchema = yup
  .object({
    financial_year: yup.string().required(),
    quarter: yup.string().required(),
    forecast_rule1: yup.string().required(),
    forecast_model: yup.string().required(),
    forecast_entry_rule1: yup.string().when("forecast_model", {
      is: (modelVal) => modelVal != 1,
      then: yup.string().required(),
    }),
    month: yup.object().shape({
      mon1: yup.object().shape({
        forecast_entry_rule1: yup.number().when("forecast_model", {
          is: "1",
          then: yup.number().positive().required(),
        }),
      }),
      mon2: yup.object().shape({
        forecast_entry_rule1: yup.number().when("forecast_model", {
          is: 1,
          then: yup.number().positive().required(),
        }),
      }),
      mon3: yup.object().shape({
        forecast_entry_rule1: yup.number().when("forecast_model", {
          is: 1,
          then: yup.number().positive().required(),
        }),
      }),
    }),
  })
  .required();

const FinanceForecast = (params) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const {
    forecast_detail,
    addRevenueRuleModal,
    editRevenueRuleModal,
    editRevenueRuleId,
    editRevenueRuleDetail,
    addCostModal,
  } = useSelector((state) => state.settingReducer);

  useEffect(() => {
    dispatch(getFinanceForecastSettings());

    return () => {
      dispatch(changeSettingValue("forecast_detail", null));
    };
  }, []);

  /* Edit Revenue Rule */
  const closeEditRevenueRuleModal = useCallback(
    (value) => {
      dispatch(changeSettingValue("editRevenueRuleModal", value));
      resetEditRevenueRuleHandler();
    },
    [editRevenueRuleModal]
  );

  const editRevenueRuleHandler = (ruleId) => {
    dispatch(editFinanceRuleDetail(ruleId));
  };

  const initialEditRevenueRuleForm = {
    id: "",
    financial_year: "",
    quarter: "",
    month: "",
    forecast_rule1: "",
    forecast_rule2: "",
    forecast_model_id: "",
    forecast_entry_rule1: "",
    forecast_entry_rule2: "",
  };
  const [editRevenueRuleInputForm, setEditRevenueRuleInputForm] = useState(
    initialEditRevenueRuleForm
  );
  useEffect(() => {
    if (checkNull(editRevenueRuleDetail?.forecast?.id, false)) {
      let newEditRevenueRuleInput = { ...editRevenueRuleInputForm };
      newEditRevenueRuleInput.id = checkNull(
        editRevenueRuleDetail?.forecast?.id
      );
      newEditRevenueRuleInput.financial_year = checkNull(
        editRevenueRuleDetail?.forecast?.financial_year
      );
      newEditRevenueRuleInput.quarter = checkNull(
        editRevenueRuleDetail?.forecast?.quarter
      );
      newEditRevenueRuleInput.month = checkNull(
        editRevenueRuleDetail?.forecast?.month
      );
      newEditRevenueRuleInput.forecast_rule1 = checkNull(
        editRevenueRuleDetail?.forecast?.forecast_rule1_id
      );
      newEditRevenueRuleInput.forecast_rule2 = checkNull(
        editRevenueRuleDetail?.forecast?.forecast_rule2_id
      );
      newEditRevenueRuleInput.forecast_model_id = checkNull(
        editRevenueRuleDetail?.forecast?.forecast_model_id
      );
      newEditRevenueRuleInput.forecast_entry_rule1 =
        editRevenueRuleDetail?.forecast?.forecast_entry_rule1;
      newEditRevenueRuleInput.forecast_entry_rule2 = checkNull(
        editRevenueRuleDetail?.forecast?.forecast_entry_rule2
      );
      resetEditRevenueRuleHandler(newEditRevenueRuleInput);
    } else {
      closeEditRevenueRuleModal(false);
    }
  }, [editRevenueRuleDetail]);

  const changeEditRevenueRuleInputHandler = (input, val) => {
    editRevenueRuleSetValue(input, val);
    setEditRevenueRuleInputForm((prevState) => ({
      ...prevState,
      [input]: val,
    }));
    if (input == "forecast_rule1") {
      dispatch(getForecastRulesDetails(val, 2));
      changeEditRevenueRuleInputHandler("forecast_rule2", "");
    }
  };

  const {
    register: editRevenueRuleReg,
    formState: { errors: editRevenueRuleErrors },
    handleSubmit: editRevenueRuleSubmit,
    control: editRevenueRuleControl,
    setValue: editRevenueRuleSetValue,
    reset: editRevenueRuleReset,
  } = useForm({
    resolver: yupResolver(editRevenueRuleSchema),
    defaultValues: initialEditRevenueRuleForm,
    mode: "all",
    reValidateMode: "onChange",
  });

  const submitEditRevenueRuleHandler = (data) => {
    console.log("submitEditRevenueRuleHandler", data);
    dispatch(updateFinanceRuleDetail(data?.id, data));
  };
  const resetEditRevenueRuleHandler = (
    resetVal = initialEditRevenueRuleForm
  ) => {
    setEditRevenueRuleInputForm(resetVal);
    editRevenueRuleReset(resetVal);
  };

  /* Revenue Rule */

  const initialMonthForm = {
    mon1: {
      forecast_entry_rule1: 0,
      forecast_entry_rule2: 0,
    },
    mon2: {
      forecast_entry_rule1: 0,
      forecast_entry_rule2: 0,
    },
    mon3: {
      forecast_entry_rule1: 0,
      forecast_entry_rule2: 0,
    },
  };
  const initialRevenueRuleForm = {
    financial_year: "",
    quarter: "",
    forecast_rule1: "",
    forecast_rule2: "",
    forecast_model: "",
    forecast_entry_rule1: "",
    forecast_entry_rule2: "",
    month: initialMonthForm,
  };
  const closeRevenueRuleModal = useCallback(
    (value) => {
      dispatch(changeSettingValue("addRevenueRuleModal", value));
      resetRevenueRuleHandler();
    },
    [addRevenueRuleModal]
  );
  const [revenueRuleInputForm, setRevenueRuleInputForm] = useState(
    initialRevenueRuleForm
  );
  // useEffect(() => {
  //   if (forecast_detail?.merchant_data) {
  //     let newRevenueRuleInput = { ...revenueRuleInputForm };

  //     resetRevenueRuleHandler(newRevenueRuleInput);
  //   }
  // }, [forecast_detail]);
  const getQuartersHandler = (financial_year, ruleType) => {
    dispatch(
      getApplicableQuarterDetails({
        year: financial_year,
        rule_type: ruleType,
      })
    );
  };
  const changeRevenueRuleInputHandler = (input, val) => {
    revenueRuleSetValue(input, val);
    setRevenueRuleInputForm((prevState) => ({ ...prevState, [input]: val }));
    if (input == "financial_year") {
      getQuartersHandler(val, 1);
      changeRevenueRuleInputHandler("quarter", "");
    }
    if (input == "forecast_rule1") {
      dispatch(getForecastRulesDetails(val));
      changeRevenueRuleInputHandler("forecast_rule2", "");
    }
  };

  const changeRevenueMonthInputHandler = (input, inputType, val) => {
    let newMonth = initialMonthForm;
    if (revenueRuleInputForm?.month) {
      newMonth = { ...revenueRuleInputForm?.month };
      newMonth[input][inputType] = val;
    }
    revenueRuleSetValue("month", newMonth);
    setRevenueRuleInputForm((prevState) => ({ ...prevState, month: newMonth }));
  };

  const {
    register: revenueRuleReg,
    formState: { errors: revenueRuleErrors },
    handleSubmit: revenueRuleSubmit,
    control: revenueRuleControl,
    setValue: revenueRuleSetValue,
    reset: revenueRuleReset,
  } = useForm({
    resolver: yupResolver(revenueRuleSchema),
    defaultValues: initialRevenueRuleForm,
    mode: "all",
    reValidateMode: "onChange",
  });

  const submitRevenueRuleHandler = (data) => {
    console.log("submitRevenueRuleHandler", data);
    dispatch(saveFinanceForecastSettings(data));
  };
  const resetRevenueRuleHandler = (resetVal = initialRevenueRuleForm) => {
    setRevenueRuleInputForm(resetVal);
    revenueRuleReset(resetVal);
  };

  const getErrorMessageForMonth = (input, inputType) => {
    let res = null;
    const errMsg = revenueRuleErrors?.month?.[input]?.[inputType]?.message;
    if (checkNull(errMsg, false)) {
      res = <ErrorContainer message={errMsg} />;
    }
    return res;
  };

  /* Cost */
  const initialCostMonthForm = {
    mon1: {
      forecast_entry_rule1: 0,
    },
    mon2: {
      forecast_entry_rule1: 0,
    },
    mon3: {
      forecast_entry_rule1: 0,
    },
  };
  const initialCostForm = {
    financial_year: "",
    quarter: "",
    forecast_rule1: "",
    forecast_model: "",
    forecast_entry_rule1: "",
    month: initialCostMonthForm,
  };
  const closeCostModal = useCallback(
    (value) => {
      dispatch(changeSettingValue("addCostModal", value));
      resetCostHandler();
    },
    [addCostModal]
  );
  const [costInputForm, setCostInputForm] = useState(initialCostForm);
  // useEffect(() => {
  //   if (forecast_detail?.merchant_data) {
  //     let newCostInput = { ...costInputForm };

  //     resetCostHandler(newCostInput);
  //   }
  // }, [forecast_detail]);
  const changeCostInputHandler = (input, val) => {
    costSetValue(input, val);
    setCostInputForm((prevState) => ({ ...prevState, [input]: val }));
    if (input == "financial_year") {
      getQuartersHandler(val, 1);
      changeCostInputHandler("quarter", "");
    }
  };

  const changeCostMonthInputHandler = (input, inputType, val) => {
    let newMonth = initialMonthForm;
    if (costInputForm?.month) {
      newMonth = { ...costInputForm?.month };
      newMonth[input][inputType] = val;
    }
    costSetValue("month", newMonth);
    setCostInputForm((prevState) => ({ ...prevState, month: newMonth }));
  };

  const {
    register: costReg,
    formState: { errors: costErrors },
    handleSubmit: costSubmit,
    control: costControl,
    setValue: costSetValue,
    reset: costReset,
  } = useForm({
    resolver: yupResolver(costSchema),
    defaultValues: initialCostForm,
    mode: "all",
    reValidateMode: "onChange",
  });

  const submitCostHandler = (data) => {
    console.log("submitCostHandler", data);
    dispatch(saveFinanceForecastSettings(data));
  };
  const resetCostHandler = (resetVal = initialCostForm) => {
    setCostInputForm(resetVal);
    costReset(resetVal);
  };

  const getErrorMessageForCostMonth = (input, inputType) => {
    let res = null;
    const errMsg = costErrors?.month?.[input]?.[inputType]?.message;
    if (checkNull(errMsg, false)) {
      res = <ErrorContainer message={errMsg} />;
    }
    return res;
  };

  return (
    <UserInterface>
      <div className="no-sidebar row" id="content">
        <Headerbar />

        <div className="inner-content row mx-0 finance-setting-chart chart-of-accounts">
          <div className="container px-0">
            <div className="top-header-sec mb-3">
              <div className="col-12 mt-3">
                <h4 className="event-alert br-btm-0">
                  Forecast Model
                  <div className="clear"></div>
                </h4>

                <div className="row justify-content-between align-items-center">
                  <div className="col-md-6 col-10">
                    <div className="search-box mt-0">
                      <form type="GET">
                        <input
                          type="text"
                          placeholder="Search"
                          name="keyword1"
                        />
                        <input type="submit" />
                      </form>
                    </div>
                  </div>
                  <div class="col-md-4 col-2 align-right">
                    <div class="field text-right">
                      <button
                        id="download"
                        class="btn green-btn-revert download-icon mr0"
                      >
                        <i class="i-icon"> </i> Download
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div
              className="accordion inv-accordion"
              id="accordionPanelsStayOpenExample"
            >
              <div className="accordion-item">
                <h2
                  className="accordion-header"
                  id="panelsStayOpen-headingEleven"
                >
                  <button
                    className="accordion-button"
                    type="button"
                    data-bs-toggle="collapse"
                    data-bs-target="#panelsStayOpen-collapseEleven"
                    aria-expanded="true"
                    aria-controls="panelsStayOpen-collapseEleven"
                  >
                    <div className="col d-flex align-items-center justify-content-between">
                      <h4 className="event-alert p-0 mb-0">Revenue Forecast</h4>
                    </div>
                  </button>
                </h2>
                <div
                  id="panelsStayOpen-collapseEleven"
                  className="accordion-collapse collapse show"
                  aria-labelledby="panelsStayOpen-headingEleven"
                >
                  <div className="">
                    <div className="row mx-0">
                      <div className="container px-0">
                        <div className="shadowclass mt-0">
                          <div class="table-responsive">
                            <table class="checklist-table remove-space inventory_table">
                              <thead>
                                <tr>
                                  <th className="second-th col-md-2">
                                    <a href="#_">Forecast Month</a>
                                  </th>
                                  <th className="col-md-3">
                                    <a href="#_">Forecast Model Type</a>
                                  </th>
                                  <th className="col-md-1">
                                    <a href="#_">Rule #1</a>
                                  </th>
                                  <th className="text-right col-md-2">
                                    Input #1
                                  </th>
                                  <th className="text-right">
                                    <a href="#_">Rule #2</a>
                                  </th>
                                  <th className="text-right">Input #2</th>
                                  <th class="action-td"> Actions </th>
                                </tr>
                              </thead>
                              <tbody>
                                {checkArray(forecast_detail?.forecast_value) &&
                                  forecast_detail?.forecast_value
                                    .filter(
                                      (p) => p?.forecast_rule1?.rule_type == 1
                                    )
                                    .map((forecast) => {
                                      return (
                                        <tr>
                                          <td class="second-td">
                                            {forecast?.month}{" "}
                                            {forecast?.financial_year}
                                          </td>
                                          <td>
                                            {forecast?.forecast_model?.name}
                                          </td>
                                          <td>
                                            {forecast?.forecast_rule1?.rule}
                                          </td>
                                          <td className="align-right">
                                            {forecast?.forecast_rule1_id != 1 &&
                                            forecast?.forecast_rule1_id != 2
                                              ? forecast_detail?.currency
                                              : ""}{" "}
                                            {forecast?.forecast_entry_rule1}
                                          </td>
                                          <td class="align-right">
                                            {forecast?.forecast_rule2?.rule ??
                                              ""}
                                          </td>
                                          <td class="align-right">
                                            {checkNull(
                                              forecast?.forecast_entry_rule2
                                            )
                                              ? `${
                                                  forecast?.forecast_rule2_id !=
                                                    1 &&
                                                  forecast?.forecast_rule2_id !=
                                                    2
                                                    ? forecast_detail?.currency
                                                    : ""
                                                } ${
                                                  forecast?.forecast_entry_rule2
                                                }`
                                              : ""}
                                          </td>
                                          <td class="align-right">
                                            <a
                                              onClick={() =>
                                                editRevenueRuleHandler(
                                                  forecast?.id
                                                )
                                              }
                                              class="circle_icon editApFinance mx-auto float-none"
                                            >
                                              <LoadingIcon
                                                is_loading={
                                                  editRevenueRuleId ==
                                                  forecast?.id
                                                }
                                                className="iconSprites edit-icon"
                                              >
                                                <div class="tooltip-sm">
                                                  {" "}
                                                  Edit{" "}
                                                </div>
                                              </LoadingIcon>
                                            </a>
                                          </td>
                                        </tr>
                                      );
                                    })}
                              </tbody>
                            </table>
                          </div>
                          <div class="my-3">
                            <button
                              class="btn green-btn-revert add-plus addSp"
                              id="addCount"
                              type="button"
                              onClick={() => closeRevenueRuleModal(true)}
                            >
                              <i class="i-icon"> </i> Add Revenue Forecast
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="accordion-item">
                <h2 className="accordion-header" id="panelsStayOpen-headingTen">
                  <button
                    className="accordion-button"
                    type="button"
                    data-bs-toggle="collapse"
                    data-bs-target="#panelsStayOpen-collapseTen"
                    aria-expanded="true"
                    aria-controls="panelsStayOpen-collapseTen"
                  >
                    <div className="col d-flex align-items-center justify-content-between">
                      <h4 className="event-alert p-0 mb-0">Cost Forecast</h4>
                    </div>
                  </button>
                </h2>
                <div
                  id="panelsStayOpen-collapseTen"
                  className="accordion-collapse collapse show"
                  aria-labelledby="panelsStayOpen-headingTen"
                >
                  <div className="">
                    <div className="row mx-0">
                      <div className="container px-0">
                        <div className="shadowclass mt-0">
                          <div class="table-responsive">
                            <table class="checklist-table remove-space inventory_table">
                              <thead>
                                <tr>
                                  <th className="second-th col-md-2">
                                    <a href="#_">Forecast Month</a>
                                  </th>
                                  <th className="col-md-3">
                                    <a href="#_">Forecast Model Type</a>
                                  </th>
                                  <th className="col-md-1">
                                    <a href="#_">Rule</a>
                                  </th>
                                  <th className="text-right col-md-2">Input</th>
                                  <th> </th>
                                  <th class="action-td"> Actions </th>
                                </tr>
                              </thead>
                              <tbody>
                                {checkArray(forecast_detail?.forecast_value) &&
                                  forecast_detail?.forecast_value
                                    .filter(
                                      (p) => p?.forecast_rule1?.rule_type == 2
                                    )
                                    .map((forecast) => {
                                      return (
                                        <tr>
                                          <td class="second-td">
                                            {forecast?.month}{" "}
                                            {forecast?.financial_year}
                                          </td>
                                          <td>
                                            {forecast?.forecast_model?.name}
                                          </td>
                                          <td>
                                            {forecast?.forecast_rule1?.rule}
                                          </td>
                                          <td className="align-right">
                                            {forecast?.forecast_rule1?.rule ==
                                            "Labor Cost"
                                              ? `${forecast_detail?.currency}${forecast?.forecast_entry_rule1}`
                                              : forecast?.forecast_entry_rule1}
                                          </td>
                                          <td></td>
                                          <td>
                                            <a
                                              onClick={() =>
                                                editRevenueRuleHandler(
                                                  forecast?.id
                                                )
                                              }
                                              class="circle_icon editApFinance mx-auto float-none"
                                            >
                                              <LoadingIcon
                                                is_loading={
                                                  editRevenueRuleId ==
                                                  forecast?.id
                                                }
                                                className="iconSprites edit-icon"
                                              >
                                                <div class="tooltip-sm">
                                                  {" "}
                                                  Edit{" "}
                                                </div>
                                              </LoadingIcon>
                                            </a>
                                          </td>
                                        </tr>
                                      );
                                    })}
                              </tbody>
                            </table>
                          </div>
                          <div class="my-3">
                            <button
                              class="btn green-btn-revert add-plus addSp"
                              id="addCount"
                              type="button"
                              onClick={() => closeCostModal(true)}
                            >
                              <i class="i-icon"> </i> Add Cost Forecast
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <ModelContainer
        title={`Add Revenue Forecast Rules`}
        open={addRevenueRuleModal}
        closeModal={closeRevenueRuleModal}
        submitModal={revenueRuleSubmit(submitRevenueRuleHandler)}
        is_loading={false}
      >
        <form action="">
          {console.log("revenueRuleErrors", revenueRuleErrors)}
          <fieldset class="">
            <ul class="form-list label-group new-form">
              <li>
                <label class="required">Select Financial Year</label>
                <div class="input-wrap">
                  <select
                    name="data[Forecast][financial_year]"
                    {...revenueRuleReg("financial_year")}
                    class="required ForecastFinancialYear"
                    id="ForecastFinancialYear"
                    onChange={(event) =>
                      changeRevenueRuleInputHandler(
                        "financial_year",
                        event?.target?.value
                      )
                    }
                    value={revenueRuleInputForm?.financial_year}
                  >
                    <option value="">Select</option>
                    {createSelectOptions(forecast_detail?.fin_year)}
                  </select>{" "}
                  {/* <input
                    type="hidden"
                    name="data[Forecast][rule_type]"
                    class="rule_type1"
                    value="1"
                    id="ForecastRuleType"
                  />{" "} */}
                  <ErrorContainer
                    errors={revenueRuleErrors}
                    name="financial_year"
                  />
                </div>
              </li>
              <li>
                <label class="required">Select Applicable Quarter</label>
                <div class="input-wrap">
                  <select
                    {...revenueRuleReg("quarter")}
                    name="data[Forecast][quarter]"
                    class="required ForecastQuarter1"
                    id="ForecastQuarter"
                    onChange={(event) =>
                      changeRevenueRuleInputHandler(
                        "quarter",
                        event?.target?.value
                      )
                    }
                    value={revenueRuleInputForm?.quarter}
                  >
                    <option value="">Select</option>
                    {createSelectOptions(forecast_detail?.quarter)}
                  </select>{" "}
                  <ErrorContainer errors={revenueRuleErrors} name="quarter" />
                </div>
              </li>
              <li>
                <label class="required">Select Forecast Rule 1</label>
                <div class="input-wrap">
                  <select
                    {...revenueRuleReg("forecast_rule1")}
                    name="data[Forecast][forecast_rule1]"
                    class="required"
                    id="ForecastForecastRule1"
                    onChange={(event) =>
                      changeRevenueRuleInputHandler(
                        "forecast_rule1",
                        event?.target?.value
                      )
                    }
                    value={revenueRuleInputForm?.forecast_rule1}
                  >
                    <option value="">Select</option>
                    {createSelectOptions(
                      forecast_detail?.forecast_rule,
                      "id",
                      "rule"
                    )}
                  </select>{" "}
                  <ErrorContainer
                    errors={revenueRuleErrors}
                    name="forecast_rule"
                  />
                </div>
              </li>
              {forecast_detail?.forecast_rule1 !== "" &&
                checkArray(forecast_detail?.forecast_rule2) && (
                  <li class="hide forecast_rule2">
                    <label class="">Select Forecast Rule 2</label>
                    <div class="input-wrap">
                      <select
                        {...revenueRuleReg("forecast_rule2")}
                        name="data[Forecast][forecast_rule2]"
                        class=""
                        id="ForecastForecastRule2"
                        onChange={(event) =>
                          changeRevenueRuleInputHandler(
                            "forecast_rule2",
                            event?.target?.value
                          )
                        }
                        value={revenueRuleInputForm?.forecast_rule2}
                      >
                        <option value="">Select</option>
                        {createSelectOptions(
                          forecast_detail?.forecast_rule2,
                          "id",
                          "rule"
                        )}
                      </select>{" "}
                      <ErrorContainer
                        errors={revenueRuleErrors}
                        name="forecast_rule2"
                      />
                    </div>
                  </li>
                )}

              <li>
                <label class="required">Select Forecast Model</label>
                <div class="input-wrap">
                  <select
                    {...revenueRuleReg("forecast_model")}
                    name="data[Forecast][forecast_model]"
                    class="required"
                    id="ForecastForecastModel"
                    onChange={(event) =>
                      changeRevenueRuleInputHandler(
                        "forecast_model",
                        event?.target?.value
                      )
                    }
                    value={revenueRuleInputForm?.forecast_model}
                  >
                    <option value="">Select</option>
                    {createSelectOptions(forecast_detail?.forecast_model)}
                  </select>{" "}
                  <ErrorContainer
                    errors={revenueRuleErrors}
                    name="forecast_model"
                  />
                </div>
              </li>
              {revenueRuleInputForm?.forecast_model != 1 && (
                <li class="forecast_entry_rule1">
                  <label class="required">Entry for Rule 1</label>
                  <div class="input-wrap">
                    <input
                      {...revenueRuleReg("forecast_entry_rule1")}
                      name="data[Forecast][forecast_entry_rule1]"
                      class="required currency"
                      step="any"
                      type="number"
                      id="ForecastForecastEntryRule1"
                      onChange={(event) =>
                        changeRevenueRuleInputHandler(
                          "forecast_entry_rule1",
                          event?.target?.value
                        )
                      }
                      value={revenueRuleInputForm?.forecast_entry_rule1}
                    />{" "}
                    <ErrorContainer
                      errors={revenueRuleErrors}
                      name="forecast_entry_rule1"
                    />
                  </div>
                </li>
              )}
              {revenueRuleInputForm?.forecast_model != 1 &&
                !["5", "6", "7", "8"].includes(
                  revenueRuleInputForm?.forecast_rule1
                ) && (
                  <li class="forecast_entry_rule2">
                    <label class="">Entry for Rule 2</label>
                    <div class="input-wrap">
                      <input
                        {...revenueRuleReg("forecast_entry_rule2")}
                        name="data[Forecast][forecast_entry_rule2]"
                        class="currency"
                        step="any"
                        type="number"
                        id="ForecastForecastEntryRule2"
                        onChange={(event) =>
                          changeRevenueRuleInputHandler(
                            "forecast_entry_rule2",
                            event?.target?.value
                          )
                        }
                        value={revenueRuleInputForm?.forecast_entry_rule2}
                      />{" "}
                      <ErrorContainer
                        errors={revenueRuleErrors}
                        name="forecast_entry_rule2"
                      />
                    </div>
                  </li>
                )}
              {revenueRuleInputForm?.forecast_model == 1 && (
                <div class="monthly_target">
                  <li>
                    <label class="required">Month 1 - Rule 1</label>
                    <div class="input-wrap">
                      <input
                        {...revenueRuleReg("month.mon1.forecast_entry_rule1")}
                        name="data[month][mon1][forecast_entry_rule1]"
                        class="required currency"
                        step="any"
                        type="number"
                        id="monthMon1ForecastEntryRule1"
                        onChange={(event) =>
                          changeRevenueMonthInputHandler(
                            "mon1",
                            "forecast_entry_rule1",
                            event?.target?.value
                          )
                        }
                        value={
                          revenueRuleInputForm?.month?.mon1
                            ?.forecast_entry_rule1
                        }
                      />{" "}
                      {getErrorMessageForMonth("mon1", "forecast_entry_rule1")}
                    </div>
                  </li>
                  <li>
                    <label class="required">Month 2 - Rule 1</label>
                    <div class="input-wrap">
                      <input
                        {...revenueRuleReg("month.mon2.forecast_entry_rule1")}
                        name="data[month][mon2][forecast_entry_rule1]"
                        class="required currency"
                        step="any"
                        type="number"
                        id="monthMon2ForecastEntryRule1"
                        onChange={(event) =>
                          changeRevenueMonthInputHandler(
                            "mon2",
                            "forecast_entry_rule1",
                            event?.target?.value
                          )
                        }
                        value={
                          revenueRuleInputForm?.month?.mon2
                            ?.forecast_entry_rule1
                        }
                      />{" "}
                      {getErrorMessageForMonth("mon2", "forecast_entry_rule1")}
                    </div>
                  </li>
                  <li>
                    <label class="required">Month 3 - Rule 1</label>
                    <div class="input-wrap">
                      <input
                        {...revenueRuleReg("month.mon3.forecast_entry_rule1")}
                        name="data[month][mon3][forecast_entry_rule1]"
                        class="required currency"
                        step="any"
                        type="number"
                        id="monthMon3ForecastEntryRule1"
                        onChange={(event) =>
                          changeRevenueMonthInputHandler(
                            "mon3",
                            "forecast_entry_rule1",
                            event?.target?.value
                          )
                        }
                        value={
                          revenueRuleInputForm?.month?.mon3
                            ?.forecast_entry_rule1
                        }
                      />{" "}
                      {getErrorMessageForMonth("mon3", "forecast_entry_rule1")}
                    </div>
                  </li>
                  {!["5", "6"].includes(
                    revenueRuleInputForm?.forecast_rule1
                  ) && (
                    <>
                      <li class="monthly_target_rule2">
                        <label class="">Month 1 - Rule 2</label>
                        <div class="input-wrap">
                          <input
                            {...revenueRuleReg(
                              "month.mon1.forecast_entry_rule2"
                            )}
                            name="data[month][mon1][forecast_entry_rule2]"
                            class="currency"
                            step="any"
                            type="number"
                            id="monthMon1ForecastEntryRule2"
                            onChange={(event) =>
                              changeRevenueMonthInputHandler(
                                "mon1",
                                "forecast_entry_rule2",
                                event?.target?.value
                              )
                            }
                            value={
                              revenueRuleInputForm?.month?.mon1
                                ?.forecast_entry_rule2
                            }
                          />{" "}
                          {getErrorMessageForMonth(
                            "mon1",
                            "forecast_entry_rule2"
                          )}
                        </div>
                      </li>
                      <li class="monthly_target_rule2">
                        <label class="">Month 2 - Rule 2</label>
                        <div class="input-wrap">
                          <input
                            {...revenueRuleReg(
                              "month.mon2.forecast_entry_rule2"
                            )}
                            name="data[month][mon2][forecast_entry_rule2]"
                            class="currency"
                            step="any"
                            type="number"
                            id="monthMon2ForecastEntryRule2"
                            onChange={(event) =>
                              changeRevenueMonthInputHandler(
                                "mon2",
                                "forecast_entry_rule2",
                                event?.target?.value
                              )
                            }
                            value={
                              revenueRuleInputForm?.month?.mon2
                                ?.forecast_entry_rule2
                            }
                          />{" "}
                          {getErrorMessageForMonth(
                            "mon2",
                            "forecast_entry_rule2"
                          )}
                        </div>
                      </li>
                      <li class="monthly_target_rule2">
                        <label class="">Month 3 - Rule 2</label>
                        <div class="input-wrap">
                          <input
                            {...revenueRuleReg(
                              "month.mon3.forecast_entry_rule2"
                            )}
                            name="data[month][mon3][forecast_entry_rule2]"
                            class="currency"
                            step="any"
                            type="number"
                            id="monthMon3ForecastEntryRule2"
                            onChange={(event) =>
                              changeRevenueMonthInputHandler(
                                "mon3",
                                "forecast_entry_rule2",
                                event?.target?.value
                              )
                            }
                            value={
                              revenueRuleInputForm?.month?.mon3
                                ?.forecast_entry_rule2
                            }
                          />{" "}
                          {getErrorMessageForMonth(
                            "mon3",
                            "forecast_entry_rule2"
                          )}
                        </div>
                      </li>
                    </>
                  )}
                </div>
              )}
            </ul>
          </fieldset>
        </form>
      </ModelContainer>
      <ModelContainer
        title={`Edit Forecast Rules`}
        open={editRevenueRuleModal}
        submitModal={editRevenueRuleSubmit(submitEditRevenueRuleHandler)}
        closeModal={closeEditRevenueRuleModal}
        is_loading={false}
      >
        <form>
          {console.log("editRevenueRuleInputForm", editRevenueRuleInputForm)}
          <fieldset class="">
            <ul class="form-list label-group new-form">
              <li>
                <label class="required">Select Financial Year</label>
                <div class="input-wrap">
                  <select
                    {...editRevenueRuleReg("financial_year")}
                    value={editRevenueRuleInputForm?.financial_year}
                    name="data[Forecast][financial_year]"
                    class="required"
                    disabled="disabled"
                    id="ForecastFinancialYear"
                  >
                    <option value="">Select</option>
                    {createSelectOptions(editRevenueRuleDetail?.fin_year)}
                  </select>{" "}
                  <ErrorContainer
                    errors={editRevenueRuleErrors}
                    name="financial_year"
                  />
                </div>
              </li>
              <li>
                <label class="required">Select Month</label>
                <div class="input-wrap">
                  <select
                    {...editRevenueRuleReg("month")}
                    value={editRevenueRuleInputForm?.month}
                    name="data[Forecast][month]"
                    class="required"
                    disabled="disabled"
                    id="ForecastMonth"
                  >
                    <option value="">Select</option>
                    <option value="January">January</option>
                    <option value="February">February</option>
                    <option value="March">March</option>
                    <option value="April">April</option>
                    <option value="May">May</option>
                    <option value="June">June</option>
                    <option value="July">July</option>
                    <option value="August">August</option>
                    <option value="September">September</option>
                    <option value="October">October</option>
                    <option value="November">November</option>
                    <option value="December">December</option>
                  </select>{" "}
                  <ErrorContainer errors={editRevenueRuleErrors} name="month" />
                </div>
              </li>
              <li>
                <label class="required">Select Forecast Rule 1</label>
                <div class="input-wrap">
                  <select
                    {...editRevenueRuleReg("forecast_rule1")}
                    name="Editdata[Forecast][forecast_rule1]"
                    class="required"
                    id="EditForecastForecastRule1Id"
                    onChange={(event) =>
                      changeEditRevenueRuleInputHandler(
                        "forecast_rule1",
                        event?.target?.value
                      )
                    }
                    value={editRevenueRuleInputForm?.forecast_rule1}
                  >
                    <option value="">Select</option>
                    {createSelectOptions(
                      editRevenueRuleDetail?.forecast_rule,
                      "id",
                      "rule"
                    )}
                  </select>{" "}
                  <ErrorContainer
                    errors={editRevenueRuleErrors}
                    name="forecast_rule"
                  />
                </div>
              </li>
              {checkNull(editRevenueRuleInputForm?.forecast_rule1, false) &&
                checkArray(editRevenueRuleDetail?.forecast_rule2) && (
                  <li class="forecast_rule2">
                    <label class="">Select Forecast Rule 2</label>
                    <div class="input-wrap">
                      <select
                        {...editRevenueRuleReg("forecast_rule2")}
                        name="Editdata[Forecast][forecast_rule2]"
                        class="required"
                        id="EditForecastForecastRule2Id"
                        onChange={(event) =>
                          changeEditRevenueRuleInputHandler(
                            "forecast_rule2",
                            event?.target?.value
                          )
                        }
                        value={editRevenueRuleInputForm?.forecast_rule2}
                      >
                        <option value="">Select</option>
                        {createSelectOptions(
                          editRevenueRuleDetail?.forecast_rule2,
                          "id",
                          "rule"
                        )}
                      </select>{" "}
                      <ErrorContainer
                        errors={editRevenueRuleErrors}
                        name="forecast_rule2"
                      />
                    </div>
                  </li>
                )}

              <li>
                <label class="required">Select Forecast Model</label>
                <div class="input-wrap">
                  <select
                    {...editRevenueRuleReg("forecast_model_id")}
                    name="Editdata[Forecast][forecast_model_id]"
                    class="required"
                    id="EditForecastForecastModelId"
                    onChange={(event) =>
                      changeEditRevenueRuleInputHandler(
                        "forecast_model_id",
                        event?.target?.value
                      )
                    }
                    value={editRevenueRuleInputForm?.forecast_model_id}
                  >
                    <option value="">Select</option>
                    {createSelectOptions(editRevenueRuleDetail?.forecast_model)}
                  </select>{" "}
                  <ErrorContainer
                    errors={editRevenueRuleErrors}
                    name="forecast_model_id"
                  />
                </div>
              </li>

              <li class="forecast_entry_rule1">
                <label class="required">Entry for Rule 1</label>
                <div class="input-wrap">
                  <input
                    {...editRevenueRuleReg("forecast_entry_rule1")}
                    name="Editdata[Forecast][forecast_entry_rule1]"
                    class="required currency"
                    step="any"
                    type="number"
                    id="EditForecastForecastEntryRule1"
                    onChange={(event) =>
                      changeEditRevenueRuleInputHandler(
                        "forecast_entry_rule1",
                        event?.target?.value
                      )
                    }
                    value={editRevenueRuleInputForm?.forecast_entry_rule1}
                  />{" "}
                  <ErrorContainer
                    errors={editRevenueRuleErrors}
                    name="forecast_entry_rule1"
                  />
                </div>
              </li>
              {checkNull(editRevenueRuleInputForm?.forecast_rule2, false) && (
                <li class="forecast_entry_rule2 hide">
                  <label class="">Entry for Rule 2</label>
                  <div class="input-wrap">
                    <input
                      {...editRevenueRuleReg("forecast_entry_rule2")}
                      name="Editdata[Forecast][forecast_entry_rule2]"
                      class="currency"
                      step="any"
                      type="number"
                      id="EditForecastForecastEntryRule2"
                      onChange={(event) =>
                        changeEditRevenueRuleInputHandler(
                          "forecast_entry_rule2",
                          event?.target?.value
                        )
                      }
                      value={editRevenueRuleInputForm?.forecast_entry_rule2}
                    />{" "}
                    <ErrorContainer
                      errors={editRevenueRuleErrors}
                      name="forecast_entry_rule2"
                    />
                  </div>
                </li>
              )}
            </ul>
          </fieldset>
        </form>
      </ModelContainer>
      <ModelContainer
        title={`Add Cost Forecast Rules`}
        open={addCostModal}
        closeModal={closeCostModal}
        submitModal={costSubmit(submitCostHandler)}
        is_loading={false}
      >
        <form action="">
          {console.log("costErrors", costErrors)}
          <fieldset class="">
            <ul class="form-list label-group new-form">
              <li>
                <label class="required">Select Forecast Rule</label>
                <div class="input-wrap">
                  <select
                    {...costReg("forecast_rule1")}
                    name="data[Forecast][forecast_rule1]"
                    class="required"
                    id="ForecastForecastRule1"
                    onChange={(event) =>
                      changeCostInputHandler(
                        "forecast_rule1",
                        event?.target?.value
                      )
                    }
                    value={costInputForm?.forecast_rule1}
                  >
                    <option value="">Select</option>
                    {createSelectOptions(
                      forecast_detail?.forecast_rule2,
                      "id",
                      "rule"
                    )}
                  </select>{" "}
                  <ErrorContainer errors={costErrors} name="forecast_rule" />
                </div>
              </li>
              <li>
                <label class="required">Select Financial Year</label>
                <div class="input-wrap">
                  <select
                    name="data[Forecast][financial_year]"
                    {...costReg("financial_year")}
                    class="required ForecastFinancialYear"
                    id="ForecastFinancialYear"
                    onChange={(event) =>
                      changeCostInputHandler(
                        "financial_year",
                        event?.target?.value
                      )
                    }
                    value={costInputForm?.financial_year}
                  >
                    <option value="">Select</option>
                    {createSelectOptions(forecast_detail?.fin_year)}
                  </select>{" "}
                  <ErrorContainer errors={costErrors} name="financial_year" />
                </div>
              </li>
              <li>
                <label class="required">Select Applicable Quarter</label>
                <div class="input-wrap">
                  <select
                    {...costReg("quarter")}
                    name="data[Forecast][quarter]"
                    class="required ForecastQuarter1"
                    id="ForecastQuarter"
                    onChange={(event) =>
                      changeCostInputHandler("quarter", event?.target?.value)
                    }
                    value={costInputForm?.quarter}
                  >
                    <option value="">Select</option>
                    {createSelectOptions(forecast_detail?.quarter)}
                  </select>{" "}
                  <ErrorContainer errors={costErrors} name="quarter" />
                </div>
              </li>

              <li>
                <label class="required">Select Forecast Model</label>
                <div class="input-wrap">
                  <select
                    {...costReg("forecast_model")}
                    name="data[Forecast][forecast_model]"
                    class="required"
                    id="ForecastForecastModel"
                    onChange={(event) =>
                      changeCostInputHandler(
                        "forecast_model",
                        event?.target?.value
                      )
                    }
                    value={costInputForm?.forecast_model}
                  >
                    <option value="">Select</option>
                    {createSelectOptions(forecast_detail?.forecast_model)}
                  </select>{" "}
                  <ErrorContainer errors={costErrors} name="forecast_model" />
                </div>
              </li>
              {costInputForm?.forecast_model != 1 && (
                <li class="forecast_entry_rule1">
                  <label class="required">Entry for Rule</label>
                  <div class="input-wrap">
                    <input
                      {...costReg("forecast_entry_rule1")}
                      name="data[Forecast][forecast_entry_rule1]"
                      class="required currency"
                      step="any"
                      type="number"
                      id="ForecastForecastEntryRule1"
                      onChange={(event) =>
                        changeCostInputHandler(
                          "forecast_entry_rule1",
                          event?.target?.value
                        )
                      }
                      value={costInputForm?.forecast_entry_rule1}
                    />{" "}
                    <ErrorContainer
                      errors={costErrors}
                      name="forecast_entry_rule1"
                    />
                  </div>
                </li>
              )}

              {costInputForm?.forecast_model == 1 && (
                <div class="monthly_target">
                  <li>
                    <label class="required">Month 1 - Rule 1</label>
                    <div class="input-wrap">
                      <input
                        {...costReg("month.mon1.forecast_entry_rule1")}
                        name="data[month][mon1][forecast_entry_rule1]"
                        class="required currency"
                        step="any"
                        type="number"
                        id="monthMon1ForecastEntryRule1"
                        onChange={(event) =>
                          changeCostMonthInputHandler(
                            "mon1",
                            "forecast_entry_rule1",
                            event?.target?.value
                          )
                        }
                        value={costInputForm?.month?.mon1?.forecast_entry_rule1}
                      />{" "}
                      {getErrorMessageForCostMonth(
                        "mon1",
                        "forecast_entry_rule1"
                      )}
                    </div>
                  </li>
                  <li>
                    <label class="required">Month 2 - Rule 1</label>
                    <div class="input-wrap">
                      <input
                        {...costReg("month.mon2.forecast_entry_rule1")}
                        name="data[month][mon2][forecast_entry_rule1]"
                        class="required currency"
                        step="any"
                        type="number"
                        id="monthMon2ForecastEntryRule1"
                        onChange={(event) =>
                          changeCostMonthInputHandler(
                            "mon2",
                            "forecast_entry_rule1",
                            event?.target?.value
                          )
                        }
                        value={costInputForm?.month?.mon2?.forecast_entry_rule1}
                      />{" "}
                      {getErrorMessageForCostMonth(
                        "mon2",
                        "forecast_entry_rule1"
                      )}
                    </div>
                  </li>
                  <li>
                    <label class="required">Month 3 - Rule 1</label>
                    <div class="input-wrap">
                      <input
                        {...costReg("month.mon3.forecast_entry_rule1")}
                        name="data[month][mon3][forecast_entry_rule1]"
                        class="required currency"
                        step="any"
                        type="number"
                        id="monthMon3ForecastEntryRule1"
                        onChange={(event) =>
                          changeCostMonthInputHandler(
                            "mon3",
                            "forecast_entry_rule1",
                            event?.target?.value
                          )
                        }
                        value={costInputForm?.month?.mon3?.forecast_entry_rule1}
                      />{" "}
                      {getErrorMessageForCostMonth(
                        "mon3",
                        "forecast_entry_rule1"
                      )}
                    </div>
                  </li>
                </div>
              )}
            </ul>
          </fieldset>
        </form>
      </ModelContainer>
    </UserInterface>
  );
};
export default FinanceForecast;
